import React, {
  useContext, useEffect, useRef, useState
} from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ProductPodContext } from './ProductPodContext';
import { ProductPodGroupContext } from './ProductPodGroupContext';

import { productPodClasses } from './utils';
import { ProductDefault } from './subcomponents/product-default/ProductDefault';
import { ProductHorizontalMobile } from './subcomponents/product-horizontal/product-horizontal-mobile';
import { ProductList } from './subcomponents/product-list/ProductList';
import { ProductMini } from './subcomponents/product-mini/ProductMini';
import { ProductMobileSingleColumn } from './subcomponents/product-mobile/ProductMobileSingleColumn';
import { ProductPodGrouped } from './subcomponents/product-pod-grouped/ProductPodGrouped';
import { ProductSideBySide } from './subcomponents/product-side-by-side/ProductSideBySide';
import {
  ProductPodPropTypes,
  ProductPodDefaultProps,
  getProductPodContextValue
} from './Helper';
import { dataModel } from './dataModel';
import { usePreFetchTimer } from './usePreFetchTimer';
import './product-pod.scss';

export const ProductPodMobile = (props) => {
  const {
    channelFromTest,
    className,
    hover,
    hoverDelay,
    installationServices,
    isEtchOne,
    itemId,
    keyword,
    miniOptions,
    mobilePodType,
    onError,
    onHover,
    onHoverExit,
    podType,
    preFetchProductData,
    showDpdPod,
    showExactPriceOnLoad,
    showSubscribeAndSave,
    storeId,
    testId,
    zipCode,
    showKPF,
    ignoreAnalytics,
    hideCompareBox
  } = props;

  const { channel, hosts } = useContext(ExperienceContext);
  const { handleProductPodExpansion, setOverlayState } = useContext(ProductPodGroupContext);

  const [state, setState] = useState({
    buyMoreSaveMore: false,
    itemId,
    isHovered: false,
    podInteractionNumber: 1,
    selectedItemId: null,
    showExactPrice: showExactPriceOnLoad,
    previousItemId: null,
    itemIdForSubComponents: itemId,
    canonicalUrl: null,
    quantity: 1,
  });

  const productRef = useRef(null);

  const itemIdProp = state.selectedItemId || itemId;

  const { data, loading, error } = useDataModel('product', {
    variables: {
      itemId: itemIdProp,
      storeId,
      zipCode,
      skipInstallServices: !installationServices,
      skipKPF: !showKPF,
      skipSubscribeAndSave: !showSubscribeAndSave
    },
    skip: !itemIdProp,
    fetchPolicy: state.fetchPolicy
  });

  if (data) {
    productRef.current = data;
  }

  useEffect(() => {
    return () => {
      // On Component un mounting close see similar item window.
      if (setOverlayState) {
        setOverlayState(false);
      }
    };
  }, []);

  useEffect(() => {
    if (data?.product) {
      setState({
        ...state,
        itemIdForSubComponents: data.product?.itemId,
        canonicalUrl: data.product?.identifiers?.canonicalUrl
      });
    }
  }, [data?.product]);

  useEffect(() => {
    if (data?.product && !data?.product?.itemId && onError) {
      onError({ data, loading, error });
    }
  }, [data?.product]);

  useEffect(() => {
    if (error && onError) {
      onError({ data, loading, error });
    }
  }, [error]);

  const productPodRef = useRef(null);

  const [onProductPodHover, onProductPodHoverExit] = usePreFetchTimer({
    onHover,
    onHoverExit,
    hoverDelay,
    preFetchProductData,
    itemId,
    storeId,
    zipCode
  });

  if ((error || loading) && !data && !productRef?.current) return null;

  const { product } = data || productRef?.current || {};
  if (!product) return null;
  if (!product.itemId) return null;

  const {
    disabled,
    onAction,
    enableATC,
    silentATC,
    onlyShowIfFulfillable,
    inCartQuantity,
    callToActionSection,
    showBadge,
    showBundleDiscount
  } = miniOptions || {};

  const context = getProductPodContextValue({
    props,
    channel,
    state,
    hosts,
    productRef,
    productPodRef,
    handleProductPodExpansion,
    product,
    setState,
    setOverlayState
  });

  return (
    <div
      className={productPodClasses({
        channel: 'mobile',
        className,
        hover,
        podType,
        mobilePodType
      })}
      data-testid={testId}
      data-automation-id="podnode"
      ref={productPodRef}
      onMouseEnter={() => onProductPodHover(itemId)}
      onMouseLeave={() => onProductPodHoverExit()}
      data-type="product"
    >
      <meta data-prop="productID" content={itemId} />
      <ProductPodContext.Provider value={context}>
        {podType === 'horizontal' && <ProductHorizontalMobile channelFromTest={channelFromTest} />}
        {podType === 'list' && (
          <ProductList
            channelFromTest={channelFromTest}
            keyword={keyword}
            hideCompareBox={hideCompareBox}
          />
        )}
        {podType === 'grouped' && <ProductPodGrouped channel={channel} />}
        {podType === 'mini' && (
          <ProductMini
            disabled={disabled}
            onAction={onAction}
            enableATC={enableATC}
            silentATC={silentATC}
            onlyShowIfFulfillable={onlyShowIfFulfillable}
            inCartQuantity={inCartQuantity}
            callToActionSection={callToActionSection}
            showBadge={showBadge}
            showBundleDiscount={showBundleDiscount}
            showKPF={showKPF}
          />
        )}
        {podType === 'sidebyside' && <ProductSideBySide />}
        {mobilePodType === 'singleColumn' ? (
          <ProductMobileSingleColumn
            isEtchOne={isEtchOne}
            showDpdPod={showDpdPod}
            showKPF={showKPF}
          />
        ) : (podType === 'default' && <ProductDefault hideCompareBox={hideCompareBox} />)}
      </ProductPodContext.Provider>
    </div>
  );
};

ProductPodMobile.propTypes = ProductPodPropTypes;

ProductPodMobile.defaultProps = ProductPodDefaultProps;

ProductPodMobile.dataModel = dataModel;

ProductPodMobile.displayName = 'ProductPodMobile';
